import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalSidebar from '../components/GlobalSidebar';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import { useTableSort } from '../hooks/useTableSort';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { PortfolioRadarPanel } from '../components/PortfolioRadarPanel';
import { FutureReturnsSection } from '../components/FutureReturnsSection';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { usePortfolioRadarSignals } from '../hooks/usePortfolioRadarSignals';
import { InvestmentCompanyForm } from '../components/InvestmentCompanyForm';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import {
  computeCompanyMetrics,
  portfolioOverallGain,
  totalCompanyValuation,
  totalExpectedReturnThisYear,
  totalInvestmentPortfolio,
  totalPortfolioCurrentValue,
  totalTicketSize,
} from '../utils/calculations';
import { buildInvestmentsKpiExplains } from '../lib/kpiExplainBuilders';
import {
  formatEuro,
  formatHoldingPeriod,
  formatMultiple,
  formatPercentPoints,
  formatSignedEuro,
} from '../utils/formatters';
import { Favicon } from '../utils/favicon';
import { newId } from '../utils/id';
import {
  flagSvgUrlForCountryCode,
  getInvestmentStatus,
  isInvestmentInactive,
} from '../utils/investmentCompanyUi';
import type { InvestmentCompany, InvestmentStatus } from '../types';

type InvestmentSortKey =
  | 'company'
  | 'ticketSize'
  | 'ownership'
  | 'valuation'
  | 'equity'
  | 'period'
  | 'exitProbability'
  | 'expectedReturn'
  | 'status';

const STATUS_SORT_ORDER: Record<InvestmentStatus, number> = {
  active: 0,
  exit: 1,
  dead: 2,
};

function signedValueClass(value: number): string {
  if (value > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (value < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

/** Asset / gain: green with +, red with − when negative. */
function assetValueNode(value: number): ReactNode {
  return <span className={signedValueClass(value)}>{formatSignedEuro(value)}</span>;
}

/** Invested capital: always shown as a red negative amount. */
function investedValueNode(value: number): ReactNode {
  const amount = Math.abs(value);
  if (amount === 0) return formatEuro(0);
  return (
    <span className="financialSnapshot-profit financialSnapshot-profit--negative">
      −{formatEuro(amount, true)}
    </span>
  );
}

function moicValueNode(moic: number | null): ReactNode {
  if (moic == null) return '—';
  const formatted = formatMultiple(moic);
  if (moic > 1) {
    return <span className="financialSnapshot-profit financialSnapshot-profit--positive">{formatted}</span>;
  }
  if (moic < 1) {
    return <span className="financialSnapshot-profit financialSnapshot-profit--negative">{formatted}</span>;
  }
  return formatted;
}

export function InvestmentsView() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { companies, setCompanies } = useAppData();
  const portfolioRadarState = usePortfolioRadarSignals(user?.uid ?? '');
  const [addOpen, setAddOpen] = useState(false);

  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<InvestmentSortKey>(
    'equity',
    'desc',
  );

  const sorted = useMemo(() => {
    return sortRows(companies, {
      company: (c) => c.name,
      ticketSize: (c) => (Number.isFinite(c.ticketSize) ? c.ticketSize : 0),
      ownership: (c) => (Number.isFinite(c.ownershipPercentage) ? c.ownershipPercentage : 0),
      valuation: (c) => computeCompanyMetrics(c).companyValuation,
      equity: (c) => computeCompanyMetrics(c).myEquityValue,
      period: (c) => new Date(c.investmentDate).getTime() || 0,
      exitProbability: (c) =>
        Number.isFinite(c.exitProbabilityThisYear) ? c.exitProbabilityThisYear : 0,
      expectedReturn: (c) => {
        const st = getInvestmentStatus(c);
        if (st === 'exit') {
          return c.exitTotalEuros != null && Number.isFinite(c.exitTotalEuros)
            ? c.exitTotalEuros
            : 0;
        }
        return computeCompanyMetrics(c).expectedReturnThisYear;
      },
      status: (c) => STATUS_SORT_ORDER[getInvestmentStatus(c)],
    });
  }, [companies, sortRows]);

  const ticketTotal = totalTicketSize(companies);
  const valuationTotal = totalCompanyValuation(companies);
  const equityTotal = totalInvestmentPortfolio(companies);
  const retY = totalExpectedReturnThisYear(companies);

  const activeCount = useMemo(
    () => companies.filter((c) => getInvestmentStatus(c) === 'active').length,
    [companies]
  );
  const investmentSlotsTotal = companies.length;

  const overallGain = useMemo(() => portfolioOverallGain(companies), [companies]);
  const portfolioValue = useMemo(() => totalPortfolioCurrentValue(companies), [companies]);
  const portfolioMoic = ticketTotal > 0 ? portfolioValue / ticketTotal : null;

  const topHolding = useMemo(() => {
    if (portfolioValue <= 0) return null;
    let maxValue = 0;
    let maxName = '';
    for (const company of companies) {
      const status = getInvestmentStatus(company);
      if (status === 'dead') continue;
      const metrics = computeCompanyMetrics(company);
      const value =
        status === 'exit' &&
        company.exitTotalEuros != null &&
        Number.isFinite(company.exitTotalEuros)
          ? company.exitTotalEuros
          : metrics.myEquityValue;
      if (value > maxValue) {
        maxValue = value;
        maxName = company.name;
      }
    }
    if (maxValue <= 0) return null;
    return { name: maxName, share: (maxValue / portfolioValue) * 100, value: maxValue };
  }, [companies, portfolioValue]);

  const weightedExitOdds = useMemo(() => {
    let weightedSum = 0;
    let weight = 0;
    for (const company of companies) {
      if (getInvestmentStatus(company) !== 'active') continue;
      const equity = computeCompanyMetrics(company).myEquityValue;
      const probability = Number.isFinite(company.exitProbabilityThisYear)
        ? company.exitProbabilityThisYear
        : 0;
      weightedSum += probability * equity;
      weight += equity;
    }
    return weight > 0 ? weightedSum / weight : null;
  }, [companies]);

  const marketCount = useMemo(() => {
    const codes = new Set(
      companies
        .map((company) => company.countryCode?.trim().toLowerCase())
        .filter((code): code is string => Boolean(code)),
    );
    return codes.size;
  }, [companies]);

  const statHints = useMemo(() => {
    const n = companies.length;
    const inactive = investmentSlotsTotal - activeCount;
    const avgTicket = n > 0 ? ticketTotal / n : 0;

    return {
      investment:
        n > 0
          ? `Mean ticket ${formatEuro(avgTicket, true)} · ${n} ${n === 1 ? 'name' : 'names'}`
          : 'Deployed capital across your PE book',
      active:
        n === 0
          ? 'Track who is still on the cap table'
          : inactive > 0
            ? `${inactive} ${inactive === 1 ? 'name' : 'names'} exited or written off`
            : 'Full portfolio still marked active',
      valuation:
        n > 0 ? 'Headline EV snapshots summed across holdings' : 'Enterprise values when you add holdings',
      equity:
        ticketTotal > 0
          ? `Roughly ${formatPercentPoints((equityTotal / ticketTotal) * 100)} of tickets paid — on paper`
          : n > 0
            ? 'Enter ticket sizes to see multiples on capital'
            : 'Your slice of those EVs after ownership',
      overall:
        ticketTotal > 0
          ? `Realised proceeds plus live marks, minus what you paid in · ${formatPercentPoints((overallGain / ticketTotal) * 100)} vs tickets paid`
          : 'Benchmark needs ticket sizes for each holding',
      expected:
        n > 0 && equityTotal > 0
          ? `About ${formatPercentPoints((retY / equityTotal) * 100)} of equity book (scenario-weighted)`
          : n > 0
            ? 'Exit probability × equity plus dividends you modelled'
            : 'Forward-looking, not guaranteed cash',
      moic:
        ticketTotal > 0
          ? 'Marks and realised proceeds ÷ capital deployed'
          : 'Add ticket sizes to benchmark the book',
      topHolding: topHolding
        ? `${topHolding.name} · ${formatEuro(topHolding.value, true)}`
        : n > 0
          ? 'Largest live or exited stake in the book'
          : 'Concentration shows up once you add holdings',
      exitOdds:
        activeCount > 0
          ? `Equity-weighted across ${activeCount} active ${activeCount === 1 ? 'name' : 'names'}`
          : 'Scenario exit odds on the active book',
      markets:
        marketCount > 0
          ? `${marketCount} ${marketCount === 1 ? 'country' : 'countries'} in the portfolio`
          : 'Set country flags on holdings to track geography',
    };
  }, [
    companies.length,
    investmentSlotsTotal,
    activeCount,
    ticketTotal,
    equityTotal,
    retY,
    overallGain,
    topHolding,
    marketCount,
  ]);

  const kpiExplains = useMemo(
    () =>
      buildInvestmentsKpiExplains({
        companies,
        ticketTotal,
        equityTotal,
        valuationTotal,
        activeCount,
        investmentSlotsTotal,
        overallGain,
        portfolioValue,
        portfolioMoic,
        topHolding,
        weightedExitOdds,
        marketCount,
      }),
    [
      companies,
      ticketTotal,
      equityTotal,
      valuationTotal,
      activeCount,
      investmentSlotsTotal,
      overallGain,
      portfolioValue,
      portfolioMoic,
      topHolding,
      weightedExitOdds,
      marketCount,
    ],
  );

  function saveNewCompany(next: InvestmentCompany) {
    const id = next.id?.trim() ? next.id : newId();
    const merged = { ...next, id };
    setCompanies((prev) => [...prev, merged]);
    setAddOpen(false);
    navigate(`/investments/${id}`);
  }

  const addInvestmentAction = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={() => setAddOpen(true)}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Add investment
      </GlobalButton>
    </>
  );

  const summarySection = (
    <>
      <ViewSectionHeader
        eyebrow="Investments"
        title="Summary"
        subtitle="Portfolio-level KPIs — deployed capital, marks, returns, and outlook."
      />

      <SortableStatGrid
        viewId="investments"
        className="grid-stats grid-stats-2"
        cards={[
          {
            id: 'equity',
            pinned: true,
            label: 'Equity total',
            node: (
              <StatCard
                netWorthHeadline
                label="Equity total"
                emoji="🥞"
                value={assetValueNode(equityTotal)}
                hint={statHints.equity}
                valueWrap
                kpiExplain={kpiExplains.equity}
              />
            ),
          },
          {
            id: 'investment',
            label: 'Investment total',
            node: (
              <StatCard
                label="Investment total"
                emoji="💳"
                value={investedValueNode(ticketTotal)}
                hint={statHints.investment}
                valueWrap
                kpiExplain={kpiExplains.investment}
              />
            ),
          },
          {
            id: 'active',
            label: 'Active investments',
            node: (
              <StatCard
                label="Active investments"
                emoji="🟢"
                value={`${activeCount}/${investmentSlotsTotal}`}
                hint={statHints.active}
                kpiExplain={kpiExplains.active}
              />
            ),
          },
          {
            id: 'valuation',
            label: 'Valuation total',
            node: (
              <StatCard
                label="Valuation total"
                emoji="📈"
                value={assetValueNode(valuationTotal)}
                hint={statHints.valuation}
                valueWrap
                kpiExplain={kpiExplains.valuation}
              />
            ),
          },
          {
            id: 'overall',
            label: 'Overall return',
            node: (
              <StatCard
                label="Overall return"
                emoji="📊"
                value={
                  <span className={signedValueClass(overallGain)}>{formatSignedEuro(overallGain)}</span>
                }
                hint={statHints.overall}
                valueWrap
                kpiExplain={kpiExplains.overall}
              />
            ),
          },
          {
            id: 'moic',
            label: 'Portfolio MOIC',
            node: (
              <StatCard
                label="Portfolio MOIC"
                emoji="🧮"
                value={moicValueNode(portfolioMoic)}
                hint={statHints.moic}
                kpiExplain={kpiExplains.moic}
              />
            ),
          },
          {
            id: 'top-holding',
            label: 'Top holding',
            node: (
              <StatCard
                label="Top holding"
                emoji="👑"
                value={topHolding ? formatPercentPoints(topHolding.share) : '—'}
                hint={statHints.topHolding}
                kpiExplain={kpiExplains['top-holding']}
              />
            ),
          },
          {
            id: 'exit-odds',
            label: 'Avg. exit odds',
            node: (
              <StatCard
                label="Avg. exit odds"
                emoji="🎲"
                value={weightedExitOdds != null ? formatPercentPoints(weightedExitOdds) : '—'}
                hint={statHints.exitOdds}
                kpiExplain={kpiExplains['exit-odds']}
              />
            ),
          },
          {
            id: 'markets',
            label: 'Markets',
            node: (
              <StatCard
                label="Markets"
                emoji="🌍"
                value={marketCount > 0 ? marketCount : '—'}
                hint={statHints.markets}
                kpiExplain={kpiExplains.markets}
              />
            ),
          },
          {
            id: 'expected',
            label: 'Expected returns (this year)',
            node: (
              <StatCard
                label="Expected returns (this year)"
                emoji="💰"
                value={assetValueNode(retY)}
                hint={statHints.expected}
                valueWrap
                kpiExplain={kpiExplains.expected}
              />
            ),
          },
        ]}
      />
    </>
  );

  const holdingsSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investments"
        title="Holdings"
        subtitle="Your cap table with ticket size, ownership, valuation, and status per company."
      />

      <section>
        {sorted.length === 0 ? (
          <GlobalEmptyState
            buttonText="Add a company"
            onButtonClick={() => setAddOpen(true)}
          />
        ) : (
          <div className="table-card">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <GlobalSortableTh sortKey="company" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Company
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="ticketSize" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Ticket size
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="ownership" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Ownership
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="valuation" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Valuation
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="equity" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Equity value
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="period" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Period
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="exitProbability" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Exit %
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="expectedReturn" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Expected return
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="status" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Status
                  </GlobalSortableTh>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const m = computeCompanyMetrics(c);
                  const inactive = isInvestmentInactive(c);
                  const st = getInvestmentStatus(c);
                  const flagUrl = flagSvgUrlForCountryCode(c.countryCode);
                  return (
                    <tr
                      key={c.id}
                      className={`click-row${inactive ? ' investment-row--inactive' : ''}`}
                      onClick={() => navigate(`/investments/${c.id}`)}
                    >
                      <td>
                        <div className="companyCell">
                          {c.websiteUrl ? (
                            <Favicon
                              websiteUrl={c.websiteUrl}
                              size={20}
                              alt={`${c.name} site`}
                              className="companyFavicon"
                            />
                          ) : (
                            <span
                              className="companyFavicon"
                              style={{
                                display: 'inline-block',
                                width: 20,
                                height: 20,
                                borderRadius: 6,
                                background: 'var(--global-separator-color)',
                              }}
                              aria-hidden
                            />
                          )}
                          <span>{c.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="ticketCell">
                          {flagUrl ? (
                            <img
                              src={flagUrl}
                              alt=""
                              className="ticketCellFlag"
                              width={15}
                              height={13}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : null}
                          <span>{investedValueNode(c.ticketSize)}</span>
                        </span>
                      </td>
                      <td>{formatPercentPoints(c.ownershipPercentage)}</td>
                      <td>{formatEuro(m.companyValuation, true)}</td>
                      <td>{assetValueNode(m.myEquityValue)}</td>
                      <td>{formatHoldingPeriod(c.investmentDate)}</td>
                      <td>{formatPercentPoints(c.exitProbabilityThisYear)}</td>
                      <td>
                        {st === 'exit'
                          ? c.exitTotalEuros != null && Number.isFinite(c.exitTotalEuros)
                            ? assetValueNode(c.exitTotalEuros)
                            : '—'
                          : assetValueNode(m.expectedReturnThisYear)}
                      </td>
                      <td className="investmentStatusCell">
                        {st === 'active' ? (
                          <span className="muted">Active</span>
                        ) : st === 'exit' ? (
                          <>
                            <span aria-hidden>🎉</span> <span>Exit</span>
                          </>
                        ) : (
                          <>
                            <span aria-hidden>💀</span> <span>Dead</span>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </section>
    </>
  );

  const radarSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investments"
        title="Portfolio radar"
        subtitle="Signals for names that need attention, follow-on review, or a missing update."
      />
      <div style={{ minWidth: 0 }}>
        <PortfolioRadarPanel companies={companies} state={portfolioRadarState} />
      </div>
    </>
  );

  const futureReturnsSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investments"
        title="Future returns"
        subtitle="Projected returns across medium (2.5×), good (10×), and amazing (20×) outcome scenarios."
      />
      <FutureReturnsSection companies={companies} onUpdateCompanies={setCompanies} />
    </>
  );

  const sections: ViewSection[] = [
    { id: 'summary', label: 'Summary', content: summarySection },
    { id: 'holdings', label: 'Holdings', content: holdingsSection },
    { id: 'future-returns', label: 'Future returns', content: futureReturnsSection },
    { id: 'radar', label: 'Portfolio radar', content: radarSection },
  ];

  return (
    <ViewPage title="Investments" actions={addInvestmentAction}>
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar isOpen={addOpen} onClose={() => setAddOpen(false)}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">New company</h2>
          <InvestmentCompanyForm
            onSubmit={saveNewCompany}
            onCancel={() => setAddOpen(false)}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
